import { finleyAssertUnreachable } from '@finley-technologies/common';
import {
  CreditAgreement,
  FormulaEngine,
} from '@finley-technologies/finley-types';

// ***************************************************
//               BASE PARAMETER CONFIGS
// ***************************************************
const _createReportingDateFormula = (): CreditAgreement.Config.Reporting.Inputs.DataAccessor.FormulaParameterConfig => ({
  type: 'formula',
  params: {
    engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
    // "reportingDate" is available via the worklease by default
    formula: 'reportingDate',
  },
});

const _createReportingPeriod = (): CreditAgreement.Config.Reporting.Inputs.DataAccessor.ReportingPeriodParameterConfig => ({
  type: 'reportingPeriod',
  params: { period: 'calendarMonth' },
});

// ***************************************************
//                  ACCESSOR CONFIGS
// ***************************************************
const createAccruedLiabilitiesConfig = (): CreditAgreement.Config.Reporting.Inputs.DataAccessor.AccruedLiabilitiesConfig => ({
  startDateFormula: _createReportingDateFormula(),
  endDateFormula: _createReportingDateFormula(),
});

// Exporting this one since it's used as the "default" placeholder
export const createBorrowingBaseMetricsConfig = (): CreditAgreement.Config.Reporting.Inputs.DataAccessor.BorrowingBaseMetricsConfig => ({
  reportingDateFormula: _createReportingDateFormula(),
  reportingPeriod: _createReportingPeriod(),
});

const createFundingRequestMetricsConfig = (): CreditAgreement.Config.Reporting.Inputs.DataAccessor.FundingRequestMetricsConfig => ({
  reportingDateFormula: _createReportingDateFormula(),
  reportingPeriod: _createReportingPeriod(),
});

const createOrgProvidedInputsConfig = (): CreditAgreement.Config.Reporting.Inputs.DataAccessor.OrgProvidedInputsConfig => ({
  reportingDateFormula: _createReportingDateFormula(),
  reportingPeriod: _createReportingPeriod(),
});

const createPortfolioCovenantResultsConfig = (): CreditAgreement.Config.Reporting.Inputs.DataAccessor.PortfolioCovenantResultsConfig => ({
  reportingDateFormula: _createReportingDateFormula(),
  reportingPeriod: _createReportingPeriod(),
});

export const updateAccessorConfigShell = (params: {
  dataAccessor: CreditAgreement.Config.Reporting.Inputs.DataAccessor.Accessor;
}): CreditAgreement.Config.Reporting.Inputs.DataAccessor.Accessor => {
  const { dataAccessor } = params;
  switch (dataAccessor.method) {
    case 'accruedLiabilities':
      return {
        ...dataAccessor,
        parameterConfig: createAccruedLiabilitiesConfig(),
      };
    case 'borrowingBaseMetrics':
      return {
        ...dataAccessor,
        parameterConfig: createBorrowingBaseMetricsConfig(),
      };
    case 'fundingRequestMetrics':
      return {
        ...dataAccessor,
        parameterConfig: createFundingRequestMetricsConfig(),
      };
    case 'orgProvidedInputs':
      return {
        ...dataAccessor,
        parameterConfig: createOrgProvidedInputsConfig(),
      };
    case 'portfolioCovenantResults':
      return {
        ...dataAccessor,
        parameterConfig: createPortfolioCovenantResultsConfig(),
      };
    default:
      return finleyAssertUnreachable(dataAccessor);
  }
};
